import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom, retry, Subject, tap, timeout, timer } from 'rxjs';
import { JWTJSON, StatoJobFiles } from 'src/app/components/app-follia-widget/chat-data-interface';
import { UtilityService } from '../utility/utility.service';
import { FolliaWidgetService } from './follia-widget.service';

@Injectable({
  providedIn: 'root',
})
export class FolliaWidgetStoreService {
  /* ----- SERVICES ------- */
  private readonly folliaWidgetService = inject(FolliaWidgetService);
  private readonly utilityService = inject(UtilityService);
  /* ----- PROMPT ------- */
  readonly tematichePromptByIdMap = signal<Map<string, string> | undefined>(undefined);
  readonly promptModulo = signal<string>('');
  readonly promptElemento = signal<string>('');
  readonly idTematica = signal<string>('');
  readonly idDatasourceAi = signal<string | undefined>(undefined);

  /* Chiave di isolamento dello storico chat del widget: ogni modulo ha il suo storico.
  Viene passata all'auth verso Follia (getJWTFollia). Per i report VSME corrisponde
  all'id del modulo: generato lato client in creazione, esistente in modifica */
  readonly idStoricoChat = signal<string | undefined>(undefined);

  /* Versione observable della chiave di isolamento: usata da refreshAuthToken()
  per attendere che la chiave esista prima di autenticarsi verso Follia */
  private readonly idStoricoChat$ = toObservable(this.idStoricoChat);

  /* Id datasource default creato nel portale della follia, per usare 
  il chatbot con un documento di default anche prima della creazione del modulo */
  readonly idDatasourceDefault = signal<string | undefined>(undefined);

  readonly chatViewablePrompt = signal<string>('');

  readonly promptTematica = computed(() => {
    return this.tematichePromptByIdMap()?.get(this.idTematica()) ?? '';
  });

  /* TOKEN */
  readonly tokenExpiration = signal<number>(0);
  readonly oldJWT = signal<JWTJSON | undefined>(undefined);

  /* Single-flight: Promise del refresh in corso. Le chiamate concorrenti a getValidToken()
  attendono questa stessa Promise invece di lanciare ognuna il proprio refresh */
  private refreshInFlight: Promise<{ success: boolean; jwt?: JWTJSON }> | null = null;

  /* Cooldown: timestamp dell'ultimo refresh fallito. Finché non passa il cooldown
  non si ritenta, per non martellare il server di auth già in difficoltà */
  private lastRefreshFailedAt = 0;
  private readonly REFRESH_FAIL_COOLDOWN_MS = 10000;

  /* FILES */
  readonly isLoadingFileFollia = signal<boolean>(false);
  readonly fileCaricati = signal<string>('');

  /* ------ CHAT ACTION ----- */
  private readonly _sendInteractionSource = new Subject<void>();
  readonly sendInteraction$ = this._sendInteractionSource.asObservable();

  readonly showChatWidget = signal(false);

  constructor() {
    // Reset solo sulla transizione true -> false (chiusura del widget).
    // Senza il controllo sul valore precedente, la prima esecuzione dell'effect
    // (con showChatWidget ancora false) cancellerebbe i valori appena impostati
    // dal chiamante (es. idStoricoChat settato nel costruttore della dialog)
    let prevShowChatWidget = false;
    effect(() => {
      const show = this.showChatWidget();
      if (prevShowChatWidget && !show) {
        this.resetStore();
      }
      prevShowChatWidget = show;
    });
  }

  /**
   * Aggiorno il promp generale del modulo
   * @param nuovoPrompt
   */
  setPromptModulo(nuovoPrompt: string) {
    this.promptModulo.set(nuovoPrompt);
  }

  /**
   * Aggiorno la mappa dei prompt della tematica
   * @param idTematica
   * @param promptTematica
   */
  updatetematichePromptByIdMap(idTematica: string, promptTematica: string) {
    this.tematichePromptByIdMap.update((currentMap) => {
      const updatedMap = new Map(currentMap);
      updatedMap.set(idTematica, promptTematica);
      return updatedMap;
    });
  }

  /**
   * Aggiorno il prompt del elemento
   * @param nuovoPrompt
   */
  setPromptElemento(nuovoPrompt: string) {
    this.promptElemento.set(nuovoPrompt);
  }

  updateSendingToChat() {
    this._sendInteractionSource.next();
  }

  /**
   * Aggiorno l'id della tematica che sto trattando
   * @param idTematica
   */
  setIdTematica(idTematica: string) {
    this.idTematica.set(idTematica);
  }

  /**
   * Aggiorno l'id dell datasource
   * @param idDatasourceAi
   */
  setIdDatasourceAi(idDatasourceAi: string) {
    // Se il datasource cambia, invalido il token in cache così il prossimo
    // getValidToken() forza una nuova auth con il datasourceId corretto
    if (this.idDatasourceAi() !== idDatasourceAi) {
      this.oldJWT.set(undefined);
      this.tokenExpiration.set(0);
      this.lastRefreshFailedAt = 0; // Nuovo contesto: la nuova auth può partire subito
    }
    this.idDatasourceAi.set(idDatasourceAi);
  }
  /**
   * Aggiorno la chiave di isolamento dello storico chat, usata per l'auth verso Follia
   * @param idStoricoChat
   */
  setIdStoricoChat(idStoricoChat: string) {
    // Se la chiave cambia, invalido il token in cache così il prossimo
    // getValidToken() forza una nuova auth con la chiave corretta
    if (this.idStoricoChat() !== idStoricoChat) {
      this.oldJWT.set(undefined);
      this.tokenExpiration.set(0);
      this.lastRefreshFailedAt = 0; // Nuovo contesto: la nuova auth può partire subito
    }
    this.idStoricoChat.set(idStoricoChat);
  }

  /**
   * Aggiorno l'id dell datasource di default
   * @param idDatasourceAi
   */
  setIdDatasourceDefault(idDatasourceDefault: string) {
    this.idDatasourceDefault.set(idDatasourceDefault);
  }

  /**
   * Aggiorno il messaggio prompt da visualizzare in chat come domanda
   * @param chatViewablePrompt
   */
  setChatViewablePrompt(chatViewablePrompt: string) {
    this.chatViewablePrompt.set(chatViewablePrompt);
  }

  /**
   * Riporta tutti i signal ai valori iniziali
   */
  private resetStore() {
    this.tematichePromptByIdMap.set(undefined);
    this.promptModulo.set('');
    this.promptElemento.set('');
    this.idTematica.set('');
    this.chatViewablePrompt.set('');
    this.idDatasourceAi.set('');
    this.idStoricoChat.set(undefined);
    this.idDatasourceDefault.set('');
    this.showChatWidget.set(false);
    this.oldJWT.set(undefined);
    this.tokenExpiration.set(0);
    this.lastRefreshFailedAt = 0;
    this.isLoadingFileFollia.set(false);
    this.fileCaricati.set('');
  }

  /**
   * Effettua il refresh del JWT con 3 tentativi di retry in caso di errore.
   * @returns Esito dell'operazione e il jwt se ottenuto.
   */
  async refreshAuthToken(): Promise<{ success: boolean; jwt?: JWTJSON }> {
    try {
      // La chiave di isolamento è OBBLIGATORIA per l'auth verso Follia: attendo che
      // esista (id del report generato lato client in creazione, o esistente in modifica)
      // prima di chiamare getJWTFollia. Se non arriva entro il timeout, fallisco.
      const idStoricoChat = await firstValueFrom(
        this.idStoricoChat$.pipe(
          filter((id): id is string => !!id),
          timeout(10000),
        ),
      );

      // Get del token per la Follia: l'auth avviene con la chiave di isolamento dello storico chat
      const jwt$ = this.folliaWidgetService.getJWTFollia(idStoricoChat).pipe(
        tap((jwt) => {
          // Se il servizio risponde ma il JWT è nullo/undefined, lancio un errore per triggerare il retry
          if (!jwt) throw new Error('Token non ricevuto');
        }),
        retry({
          count: 3, // Numero massimo di tentativi
          delay: (error, retryCount) => {
            console.warn(`Tentativo di refresh #${retryCount} fallito:`, error);
            // Backoff esponenziale: 1s, 2s, 4s tra un tentativo e l'altro
            return timer(1000 * Math.pow(2, retryCount - 1));
          },
        }),
      );

      // Mi è ritronato il jwt
      const jwt = await firstValueFrom(jwt$);
      return { success: true, jwt };
    } catch (error) {
      // Se TUTTI i 3 tentativi falliscono mando messaggio di errore
      console.error('Refresh token fallito dopo 3 tentativi:', error);

      this.utilityService.openSnackBar(`Errore all'autenticazione verso Follia`, '', {
        duration: 3000,
        panelClass: ['red-snackbar'],
      });

      return { success: false };
    }
  }

  /**
   * Gestisce la logica di caching e recupero del token.
   * @returns Il token valido (esistente o nuovo).
   */
  async getValidToken(): Promise<{ success: boolean; updated: boolean; jwt?: JWTJSON }> {
    const now = Date.now();

    // 1. Restituisce il token se ancora valido (con un margine di 30 secondi per sicurezza)
    if (this.oldJWT()?.access_token && now < this.tokenExpiration() - 30000) {
      return { success: true, updated: false, jwt: this.oldJWT() };
    }

    // 2. Cooldown: se un refresh è appena fallito (con tutti i suoi retry),
    // non ritento subito ad ogni richiesta del widget
    if (now - this.lastRefreshFailedAt < this.REFRESH_FAIL_COOLDOWN_MS) {
      return { success: false, updated: false };
    }

    // 3. Single-flight: se un refresh è già in corso mi accodo a quello,
    // altrimenti lo lancio io. Evita N refresh paralleli da chiamate concorrenti
    this.refreshInFlight ??= this.refreshAuthToken().finally(() => {
      this.refreshInFlight = null;
    });

    const result = await this.refreshInFlight;

    if (result.success && result.jwt) {
      this.oldJWT.set(result.jwt);
      // Calcola il timestamp di scadenza (es. expires_in è in secondi)
      this.tokenExpiration.set(Date.now() + result.jwt.expires_in * 1000);
    } else {
      this.lastRefreshFailedAt = Date.now();
    }

    return { success: result.success, updated: true, jwt: result.jwt };
  }

  /**
   * Get dei jobs sullo stato dei file
   * @returns
   */
  async getStatoFile(): Promise<{ success: boolean; statoFile?: StatoJobFiles }> {
    const datasourceIds = this.idDatasourceAi();
    const { success, jwt } = await this.getValidToken();

    if (!success || !jwt || !datasourceIds) return { success: false };

    const { tenant, company, access_token } = jwt;

    try {
      // Oggetto del job che ha lo stato dei file
      const statoFile: StatoJobFiles = await firstValueFrom(
        this.folliaWidgetService.getStatoFile(tenant, company, datasourceIds, access_token),
      );

      return { success: true, statoFile: statoFile };
    } catch (error: any) {
      // Se l'errore è 401 refresh del token
      if (error?.status === 401 || error === 401) {
        console.warn('Token scaduto, provo il refresh...');

        const { success, jwt } = await this.getValidToken();

        if (!success || !jwt) return { success: false };

        if (success) {
          // Riprova la chiamata (Recursion)
          return await this.getStatoFile();
        }
      }

      // Errore generico dell'api della follia
      console.error('Errore generico di Follia:', error);
      return { success: false };
    }
  }

  /**
   * Verifica se tutti i file del job sono stati processati.
   *
   * @param statoFile L'oggetto StatoJobFiles ricevuto dal server
   * @returns * Ritorna true solo se tutti i jobs hanno come processing file 0 (vuoldire che gli ha processati tutti)
   */
  async checkIfAllItemsAreProcessed(): Promise<boolean> {
    const { success, statoFile } = await this.getStatoFile();

    // Se non ci sono job, allora vuoldire che non sono stati inseriti file, non controllo
    if (!success || !statoFile || !statoFile.items || statoFile.items.length === 0) {
      return true;
    }

    // Se tutti i file sono stati processati ritorno true
    return statoFile.items.every((item) => {
      const allProcessing = item.statusDetails.find((d) => d.status === 'PROCESSING')?.count || 0;

      // Il Job è completo se:
      // 1. Se PROCESSING è 0
      return allProcessing === 0;
    });
  }

  /**
   * Aggiorna il signal di contatori del file in tempo reale
   * esempio : (1/20), (2/20), (3/20)
   * @param statoFile i jobs dei file
   * @returns true se tutti i file sono stati processatti
   */
  updateLiveCounter(statoFile: StatoJobFiles): boolean {
    // 1. Gestione caso array vuoto o nullo
    if (!statoFile.items || statoFile.items.length === 0) {
      this.fileCaricati.set('0/0');
      return true; // Considero processato se non c'è nulla
    }

    let totalFilesGlobal = 0;
    let processedFilesGlobal = 0;
    let allItemsFinished = true;

    for (const item of statoFile.items) {
      // Calcolo totali per il contatore visivo
      const itemTotal = item.statusDetails.reduce((sum, d) => sum + d.count, 0);
      const itemProcessed = item.statusDetails.find((d) => d.status === 'PROCESSED')?.count || 0;

      totalFilesGlobal += itemTotal;
      processedFilesGlobal += itemProcessed;

      // Logica di controllo: se anche solo un item ha PROCESSING > 0, il risultato sarà false
      const currentProcessing =
        item.statusDetails.find((d) => d.status === 'PROCESSING')?.count || 0;
      if (currentProcessing > 0) {
        allItemsFinished = false;
      }
    }

    // 2. Aggiorna il segnale visivo
    this.fileCaricati.set(`${processedFilesGlobal}/${totalFilesGlobal}`);

    // 3. Ritorna lo stato del controllo
    return allItemsFinished;
  }

  /**
   * Alert che mostro all'utente finale se c'è alemno un file non processato
   */
  alertFileNotProcessed(): Promise<boolean> {
    const titolo = 'Analisi documenti in corso';
    const descrizione = `L'intelligenza artificiale sta ancora elaborando alcuni dei file caricati.<br><br> 
                       Ti preghiamo di attendere un istante affinché la lettura sia completa <br> 
                       per garantirti risposte accurate.`;

    return new Promise<boolean>((resolve) => {
      this.utilityService.openDialog({
        titolo,
        descrizione,
        fontWeight: '500',
        bottoni: [
          {
            nome_btn: 'Ho capito',
            background_color: 'var(--mat-color-accent)',
            handler: () => resolve(true),
          },
        ],
      });
    });
  }

  /**
   * Viene chiamata ricorsivamente il metodo per lo stato dei file (/jobs)
   * se i file non sono stati tutti processati allora continuo a chiamare finche non tutti processati
   * @returns
   */
  async fileLoadingRecursive(): Promise<boolean> {
    // Attiviamo lo stato di caricamento
    this.isLoadingFileFollia.set(true);

    try {
      const { success, statoFile } = await this.getStatoFile();

      // 1. Controllo se la chiamata ha avuto successo e ci sno i jobs
      if (success && statoFile) {
        const isDone = this.updateLiveCounter(statoFile);

        if (isDone) {
          // Quando tutti i file sono processati (isDone === true), abbiamo finito
          this.isLoadingFileFollia.set(false);
          return true;
        }
      } else {
        console.warn('Recupero stato file fallito, riprovo...');
        throw new Error('Recupero stato file fallito, riprovo...');
      }

      // 2. Se ci sono ancora file da processare (isDone === false) ritendo ogni n secondi
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // 3. Chiamata ricorsiva
      return await this.fileLoadingRecursive();
    } catch (error) {
      // In caso di errore critico, resettiamo lo stato e usciamo
      this.isLoadingFileFollia.set(false);
      console.error('Errore durante il caricamento:', error);
      return false;
    }
  }
}
